import { useEffect, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useEditMode,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { resolveHref } from '../utils/resolveHref';
import { useCtaColorVars } from '../hooks/useCtaColorVars';
import { useLocalizedSpelling } from '../hooks/useLocalizedSpelling';
import { onColorLinksCss } from '../utils/onColorLinks';

// ============================================================================
// Four alternating image / text feature rows — same editorial rhythm as the
// reference page (Better-Built Living, Events Schedule, Flexible Living,
// Rewards). Row 1 and 3 render image-left; rows 2 and 4 flip image-right.
// Each row's content is a self-contained slot group so the editor (and the
// per-property generator) can swap image + copy + CTA independently.
// ============================================================================

const row1Def: SlotGroupDefinition = {
  label: 'Row 1 — Signature Lifestyle',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[3].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[3].alt_text',
      defaultValue: 'Community lifestyle imagery',
    },
    eyebrow: {
      label: 'Eyebrow',
      type: 'text',
      defaultValue: 'The Experience',
    },
    title: {
      label: 'Heading',
      type: 'text',
      defaultValue: "What 'Better-Built Living' Means For You",
    },
    body: {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>From construction methods to the smallest finish, every detail is designed to make daily life more comfortable, quieter, and more connected to the neighborhood around you.</p>',
    },
    url: {
      label: 'CTA URL',
      type: 'url',
      defaultValue: '/amenities',
    },
    label: {
      label: 'CTA Label',
      type: 'text',
      defaultValue: 'Be Informed',
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
  generator: {
    prompt:
      'Lead row introducing the signature characteristic that sets this property apart (construction quality, resident-first design, sustainability, a distinct architectural approach). Write a short eyebrow, a punchy headline, a 2–3 sentence body, and a CTA whose destination is a page that exists on the site. Pick a gallery image that shows construction, exteriors, or the broader community feel.',
    context:
      'Row 1 of 4 in the alternating lifestyle feature rows. Image renders on the LEFT on desktop. Tone-setting — broadest statement of what makes this community distinct. Do not reuse the image, heading, or idea in rows 2–4.',
    length: 'Headline ≤ 8 words. Body 2–3 sentences.',
  },
};

const row2Def: SlotGroupDefinition = {
  label: 'Row 2 — Events & Community',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[4].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[4].alt_text',
      defaultValue: 'Resident gathering',
    },
    eyebrow: {
      label: 'Eyebrow',
      type: 'text',
      defaultValue: 'Community',
    },
    title: {
      label: 'Heading',
      type: 'text',
      defaultValue: 'See the Schedule of Events',
    },
    body: {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>From food-truck nights to resident mixers and seasonal celebrations, our calendar is designed to help neighbors become friends. Check in anytime for what is coming up next.</p>',
    },
    url: {
      label: 'CTA URL',
      type: 'url',
      defaultValue: '/contact-us',
    },
    label: {
      label: 'CTA Label',
      type: 'text',
      defaultValue: 'View Calendar',
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
  generator: {
    prompt:
      'Second row — focus on the social / resident-events side of life here. Describe the recurring programming the leasing team actually runs (mixers, food trucks, holiday events, resident classes). Pick a gallery image that evokes gathering, a common lounge, or the clubroom. CTA should link to a page that exists (contact-us for calendar, or resident-access).',
    context:
      'Row 2 of 4 in the alternating lifestyle feature rows. Image renders on the RIGHT on desktop. Focus on the human / social side of the community. Do not reuse the image, heading, or idea from rows 1, 3, or 4.',
    length: 'Headline ≤ 8 words. Body 2–3 sentences.',
  },
};

const row3Def: SlotGroupDefinition = {
  label: 'Row 3 — Flexible Living',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[5].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[5].alt_text',
      defaultValue: 'Flexible living options',
    },
    eyebrow: {
      label: 'Eyebrow',
      type: 'text',
      defaultValue: 'Flexibility',
    },
    title: {
      label: 'Heading',
      type: 'text',
      defaultValue: 'Learn More About Our Flexible Living Programs',
    },
    body: {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>Choose the lease length and move-in options that fit your life — from standard terms to short-term stays, furnished residences, and renewal tools built to reduce your total cost of living.</p>',
    },
    url: {
      label: 'CTA URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'CTA Label',
      type: 'text',
      defaultValue: 'See Options',
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
  generator: {
    prompt:
      'Third row — focus on flexibility the property offers (lease terms, furnished options, work-from-home spaces, live/work program, pay-schedule tools, renewal flexibility). Pick a gallery image of a staged apartment interior that reads as home-y and flexible. CTA should point to the page that lists these options (floor-plans is a safe fallback).',
    context:
      'Row 3 of 4 in the alternating lifestyle feature rows. Image renders on the LEFT on desktop. Focus on lease / living arrangement flexibility. Do not reuse the image, heading, or idea from rows 1, 2, or 4.',
    length: 'Headline ≤ 10 words. Body 2–3 sentences.',
  },
};

const row4Def: SlotGroupDefinition = {
  label: 'Row 4 — Resident Rewards',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[6].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[6].alt_text',
      defaultValue: 'Resident amenity space',
    },
    eyebrow: {
      label: 'Eyebrow',
      type: 'text',
      defaultValue: 'Perks',
    },
    title: {
      label: 'Heading',
      type: 'text',
      defaultValue: 'Learn More About Our Rewards Programs',
    },
    body: {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>Rent on time, refer a friend, or renew your lease and unlock perks, local partner discounts, and resident-only offers that make it even easier to call this place home.</p>',
    },
    url: {
      label: 'CTA URL',
      type: 'url',
      defaultValue: '/resident-access',
    },
    label: {
      label: 'CTA Label',
      type: 'text',
      defaultValue: 'See Rewards',
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
  generator: {
    prompt:
      'Fourth row — focus on resident rewards, referral bonuses, or renewal perks. If the property has no formal program, write about resident appreciation, local partner discounts, or anything that functions as a reward. Pick a gallery image of an amenity space residents actually use (lounge, café, co-work, clubroom). CTA should link to resident-access or contact-us.',
    context:
      'Row 4 of 4 in the alternating lifestyle feature rows. Image renders on the RIGHT on desktop. Focus on loyalty / rewards for residents. Do not reuse the image, heading, or idea from rows 1, 2, or 3.',
    length: 'Headline ≤ 10 words. Body 2–3 sentences.',
  },
};

// ============================================================================
// Reusable row renderer. Color variant and image side alternate by the row's
// position among the rows that render, so hidden rows don't break the rhythm.
// ============================================================================

type FeatureRowValues = ReturnType<typeof useSlotGroup>;

interface FeatureRowProps {
  path: string;
  definition: SlotGroupDefinition;
  row: FeatureRowValues;
  index: number;
  isLast: boolean;
  hiddenOnLive: boolean;
}

function useIsEditing(): boolean {
  const editMode = useEditMode();
  const [inFrame, setInFrame] = useState(false);
  useEffect(() => {
    setInFrame(window.self !== window.top);
  }, []);
  return !!editMode?.isEditMode || inFrame;
}

const CONTACT_PATHS = new Set(['/contact', '/contact-us']);

/** Rows whose CTA is empty or still points at the contact page are
 *  placeholders waiting on a real destination, so they stay off the live page. */
function hasCustomLink(url: string | null | undefined): boolean {
  const href = resolveHref(url)?.trim();
  if (!href || href === '#') return false;
  const path = href.split(/[?#]/)[0].toLowerCase().replace(/\/+$/, '');
  return !CONTACT_PATHS.has(path);
}

function FeatureRow({ path, definition, row, index, isLast, hiddenOnLive }: FeatureRowProps) {
  const ref = useScrollReveal<HTMLDivElement>();

  // Alternation strategy mirrors the homepage rhythm:
  // - Rows 1, 3 (`--light`): flat white section with primary-color copy —
  //   same voice as PropertyOverview.
  // - Rows 2, 4 (`--dark`): primary-color angled band with white copy —
  //   same visual language as ConvenienceCallout / FloorplansList.
  // Image side alternates in lockstep so each row faces its neighbor.
  const dark = index % 2 === 1;
  const flipped = index % 2 === 1;
  const hasCta = !!(row.url && row.label);

  const variantClass = dark ? ' lfr-row--dark' : ' lfr-row--light';
  const flipClass = flipped ? ' lfr-row--flip' : '';
  const lastClass = isLast ? ' lfr-row--last' : '';

  return (
    <article
      ref={ref}
      className={`reveal-fade-up lfr-row${variantClass}${flipClass}${lastClass}`}
    >
      {/* Angled primary band — only on dark rows. White rows render flat so
          they read as the editorial counterpoint to the dark bands, mirroring
          the home page's PropertyOverview → ConvenienceCallout alternation. */}
      {dark ? <div className="lfr-row-band" aria-hidden="true" /> : null}

      <div className="lfr-row-inner">
        {hiddenOnLive ? (
          <p className="lfr-hidden-note" role="note">
            <strong>Hidden on the live site.</strong> This row's button still
            links to the contact page (or has no link). Add a custom CTA URL
            and it will appear automatically.
          </p>
        ) : null}
        <EditableSlotGroup groupPath={path} definition={definition}>
          <div className="lfr-row-grid">
            <div className="lfr-media">
              {row.image ? (
                <VibesitesImage
                  url={row.image}
                  alt={row.alt || row.title || ''}
                  sizes="(min-width: 1024px) 45vw, 90vw"
                  className="lfr-media-img"
                />
              ) : (
                <div className="lfr-media-placeholder" />
              )}
            </div>

            <div className="lfr-copy">
              {row.eyebrow ? (
                <span className="lfr-eyebrow">{row.eyebrow}</span>
              ) : null}
              {row.title ? <h3 className="lfr-title">{row.title}</h3> : null}
              {row.body ? (
                <div
                  className="lfr-body"
                  data-vibesite-richtext=""
                  dangerouslySetInnerHTML={{ __html: row.body }}
                />
              ) : null}
              {hasCta ? (
                <a
                  className="lfr-cta"
                  href={resolveHref(row.url || '#')}
                  target={row._target || '_self'}
                >
                  <span>{row.label}</span>
                  <svg
                    aria-hidden="true"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="square"
                    strokeLinejoin="miter"
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </a>
              ) : null}
            </div>
          </div>
        </EditableSlotGroup>
      </div>
    </article>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function LifestyleFeatureRows() {
  useRevealStyles();
  const ctaColorVars = useCtaColorVars();
  const isEditMode = useIsEditing();
  const localize = useLocalizedSpelling();

  // Only defaults are respelled; operator-edited copy renders as written.
  const rows = [
    { path: 'lifestylefeaturerows.row_1', def: row1Def, values: useSlotGroup('lifestylefeaturerows.row_1', row1Def) },
    { path: 'lifestylefeaturerows.row_2', def: row2Def, values: useSlotGroup('lifestylefeaturerows.row_2', row2Def) },
    { path: 'lifestylefeaturerows.row_3', def: row3Def, values: useSlotGroup('lifestylefeaturerows.row_3', row3Def) },
    { path: 'lifestylefeaturerows.row_4', def: row4Def, values: useSlotGroup('lifestylefeaturerows.row_4', row4Def) },
  ].map((row) =>
    row.values.body === row.def.fields.body?.defaultValue
      ? { ...row, values: { ...row.values, body: localize(row.values.body as string) } }
      : row,
  );

  // The editor keeps every row so placeholder rows can still be given a link.
  // Light/dark alternation is computed over the rows that actually render.
  const visibleRows = isEditMode
    ? rows
    : rows.filter((row) => hasCustomLink(row.values.url));

  return (
    <section className="lfr-section" style={ctaColorVars}>
      <div className="lfr-container">
        <div className="lfr-rows">
          {visibleRows.map((row, i) => (
            <FeatureRow
              key={row.path}
              path={row.path}
              definition={row.def}
              row={row.values}
              index={i}
              isLast={i === visibleRows.length - 1}
              hiddenOnLive={!hasCustomLink(row.values.url)}
            />
          ))}
        </div>

        {/* Schema-lint anchors — hidden, never interactive. The visible rows
            above pass dynamic paths to <EditableSlotGroup>, so the source
            linter can't see each literal path. These anchors carry the
            canonical paths so `slot-group-missing-editable-wrapper` matches. */}
        <EditableSlotGroup
          groupPath="lifestylefeaturerows.row_1"
          definition={row1Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="lifestylefeaturerows.row_2"
          definition={row2Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="lifestylefeaturerows.row_3"
          definition={row3Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="lifestylefeaturerows.row_4"
          definition={row4Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
      </div>

      <style>{`
        ${onColorLinksCss('.lfr-row--dark [data-vibesite-richtext]')}
        /* ── Section wrapper ── */
        /* Transparent so white rows show the page background and dark-row
           angled wedges reveal the adjacent white row underneath them. */
        .lfr-section {
          position: relative;
          background: transparent;
          padding: 0;
        }
        .lfr-container {
          position: relative;
          padding: 0;
        }
        .lfr-rows {
          display: flex;
          flex-direction: column;
        }

        /* ── Row (shared) ── */
        .lfr-row {
          position: relative;
          overflow: hidden;
          padding: 5rem 1.5rem;
        }
        @media (min-width: 768px) {
          .lfr-row { padding: 6rem 2rem; }
        }
        @media (min-width: 1024px) {
          .lfr-row { padding: 7rem 2.5rem; }
        }

        /* ── Dark variant (rows 1, 3) ── */
        /* Angled primary-color band — same visual language as
           ConvenienceCallout. Dark rows need extra top/bottom padding to
           clear the diagonal clip inset. Angle direction alternates per
           dark row so stacked dark bands read as a continuous zig-zag. */
        .lfr-row--dark {
          padding-top: 7rem;
          padding-bottom: 7rem;
        }
        @media (min-width: 768px) {
          .lfr-row--dark { padding-top: 8rem; padding-bottom: 8rem; }
        }
        @media (min-width: 1024px) {
          .lfr-row--dark { padding-top: 10rem; padding-bottom: 10rem; }
        }

        .lfr-row-band {
          position: absolute;
          inset: 0;
          background: var(--vibe-color-primary);
          clip-path: polygon(0 0, 100% 5%, 100% 100%, 0 95%);
          -webkit-clip-path: polygon(0 0, 100% 5%, 100% 100%, 0 95%);
        }
        .lfr-row--flip .lfr-row-band {
          clip-path: polygon(0 5%, 100% 0, 100% 95%, 0 100%);
          -webkit-clip-path: polygon(0 5%, 100% 0, 100% 95%, 0 100%);
        }
        @media (min-width: 768px) {
          .lfr-row-band {
            clip-path: polygon(0 0, 100% 8%, 100% 100%, 0 92%);
            -webkit-clip-path: polygon(0 0, 100% 8%, 100% 100%, 0 92%);
          }
          .lfr-row--flip .lfr-row-band {
            clip-path: polygon(0 8%, 100% 0, 100% 92%, 0 100%);
            -webkit-clip-path: polygon(0 8%, 100% 0, 100% 92%, 0 100%);
          }
        }
        @media (min-width: 1024px) {
          .lfr-row-band {
            clip-path: polygon(0 0, 100% 12%, 100% 100%, 0 88%);
            -webkit-clip-path: polygon(0 0, 100% 12%, 100% 100%, 0 88%);
          }
          .lfr-row--flip .lfr-row-band {
            clip-path: polygon(0 12%, 100% 0, 100% 88%, 0 100%);
            -webkit-clip-path: polygon(0 12%, 100% 0, 100% 88%, 0 100%);
          }
        }

        /* A dark last row ends flat so it meets the next section on a
           straight line instead of leaving a white wedge. */
        .lfr-row--dark.lfr-row--last { padding-bottom: 5rem; }
        .lfr-row--dark.lfr-row--last .lfr-row-band {
          clip-path: polygon(0 0, 100% 5%, 100% 100%, 0 100%);
          -webkit-clip-path: polygon(0 0, 100% 5%, 100% 100%, 0 100%);
        }
        .lfr-row--flip.lfr-row--last .lfr-row-band {
          clip-path: polygon(0 5%, 100% 0, 100% 100%, 0 100%);
          -webkit-clip-path: polygon(0 5%, 100% 0, 100% 100%, 0 100%);
        }
        @media (min-width: 768px) {
          .lfr-row--dark.lfr-row--last { padding-bottom: 6rem; }
          .lfr-row--dark.lfr-row--last .lfr-row-band {
            clip-path: polygon(0 0, 100% 8%, 100% 100%, 0 100%);
            -webkit-clip-path: polygon(0 0, 100% 8%, 100% 100%, 0 100%);
          }
          .lfr-row--flip.lfr-row--last .lfr-row-band {
            clip-path: polygon(0 8%, 100% 0, 100% 100%, 0 100%);
            -webkit-clip-path: polygon(0 8%, 100% 0, 100% 100%, 0 100%);
          }
        }
        @media (min-width: 1024px) {
          .lfr-row--dark.lfr-row--last { padding-bottom: 7rem; }
          .lfr-row--dark.lfr-row--last .lfr-row-band {
            clip-path: polygon(0 0, 100% 12%, 100% 100%, 0 100%);
            -webkit-clip-path: polygon(0 0, 100% 12%, 100% 100%, 0 100%);
          }
          .lfr-row--flip.lfr-row--last .lfr-row-band {
            clip-path: polygon(0 12%, 100% 0, 100% 100%, 0 100%);
            -webkit-clip-path: polygon(0 12%, 100% 0, 100% 100%, 0 100%);
          }
        }

        /* ── Light variant (rows 2, 4) ── */
        .lfr-row--light {
          background: #ffffff;
        }

        /* ── Editor-only note for rows hidden on the live site ── */
        .lfr-hidden-note {
          margin: 0 0 1.5rem;
          padding: 0.75rem 1rem;
          background: #fff7e0;
          color: #5c4500;
          border: 1px dashed #c99a00;
          font-family: system-ui, sans-serif;
          font-size: 0.875rem;
          line-height: 1.45;
        }

        /* ── Inner layout (shared) ── */
        .lfr-row-inner {
          position: relative;
          z-index: 1;
          max-width: 1200px;
          margin: 0 auto;
        }

        .lfr-row-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
          align-items: center;
        }
        @media (min-width: 1024px) {
          .lfr-row-grid {
            grid-template-columns: 1fr 1fr;
            gap: 4rem;
          }
          /* Flipped rows place the image on the right. On mobile the image
             always sits first for consistent reading rhythm. */
          .lfr-row--flip .lfr-row-grid > .lfr-media {
            order: 2;
          }
        }

        /* ── Media ── */
        .lfr-media {
          position: relative;
          overflow: hidden;
          aspect-ratio: 4 / 3;
        }
        .lfr-row--dark .lfr-media {
          background: color-mix(in srgb, var(--vibe-color-primary) 70%, #000);
          box-shadow: 0 24px 48px -16px rgba(0, 0, 0, 0.5);
        }
        .lfr-row--light .lfr-media {
          background: var(--vibe-color-surface, #f3f4f6);
          box-shadow: 0 18px 36px -16px rgba(0, 0, 0, 0.2);
        }
        .lfr-media-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.9s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .lfr-row:hover .lfr-media-img {
          transform: scale(1.04);
        }
        .lfr-media-placeholder {
          position: absolute;
          inset: 0;
        }
        .lfr-row--dark .lfr-media-placeholder {
          background: linear-gradient(
            135deg,
            color-mix(in srgb, var(--vibe-color-primary) 85%, #000) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 60%, #000) 100%
          );
        }
        .lfr-row--light .lfr-media-placeholder {
          background: linear-gradient(135deg, var(--vibe-color-surface, #e5e7eb), #f9fafb);
        }

        /* ── Copy ── */
        .lfr-copy {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          padding: 0.25rem 0;
        }

        .lfr-eyebrow {
          font-family: var(--vibe-font-body);
          font-size: 0.78rem;
          letter-spacing: 0.3em;
          text-transform: uppercase;
        }
        .lfr-row--dark  .lfr-eyebrow { color: rgba(255, 255, 255, 0.78); }
        .lfr-row--light .lfr-eyebrow { color: var(--vibe-color-secondary); }

        .lfr-title {
          margin: 0;
          font-family: var(--vibe-font-heading);
          font-weight: 400;
          font-size: clamp(1.75rem, 3.2vw, 2.75rem);
          line-height: 1.08;
          letter-spacing: 0.03em;
        }
        .lfr-row--dark  .lfr-title { color: #ffffff; }
        .lfr-row--light .lfr-title { color: var(--vibe-color-secondary, #3E441D); }

        .lfr-body,
        .lfr-body * {
          font-family: var(--vibe-font-body);
        }
        .lfr-body {
          font-size: 0.9375rem;
          line-height: 1.65;
          max-width: 32rem;
        }
        @media (min-width: 768px) {
          .lfr-body { font-size: 1rem; }
        }
        .lfr-row--dark  .lfr-body { color: rgba(255, 255, 255, 0.9); }
        .lfr-row--light .lfr-body { color: rgba(17, 24, 39, 0.8); }
        .lfr-body p { margin: 0 0 0.55rem; }
        .lfr-body p:last-child { margin-bottom: 0; }

        /* ── CTA ── */
        /* Both variants render the same secondary-filled button; hover
           inversion target differs by variant so contrast stays strong on
           its row's background. */
        .lfr-cta {
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
          margin-top: 0.75rem;
          padding: 1rem 2.5rem;
          background: var(--cta-bg, var(--vibe-color-secondary));
          color: var(--cta-text, var(--vibe-color-text-on-secondary, #ffffff));
          border: 1.5px solid var(--cta-bg, var(--vibe-color-secondary));
          font-family: var(--vibe-font-heading);
          font-size: 0.8125rem;
          font-weight: 400;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          text-decoration: none;
          transition: background-color 0.25s ease, color 0.25s ease,
                      transform 0.25s ease, border-color 0.25s ease;
          align-self: flex-start;
        }
        .lfr-cta svg {
          transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .lfr-row--dark .lfr-cta:hover {
          background: #ffffff;
          color: var(--cta-bg, var(--vibe-color-secondary));
          border-color: #ffffff;
          transform: translateY(-2px);
        }
        .lfr-row--light .lfr-cta:hover {
          background: #ffffff;
          color: var(--cta-bg, var(--vibe-color-secondary));
          border-color: var(--cta-bg, var(--vibe-color-secondary));
          transform: translateY(-2px);
        }
        .lfr-cta:hover svg {
          transform: translateX(4px);
        }

        @media (max-width: 639px) {
          .lfr-cta { width: 100%; justify-content: center; }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'LifestyleFeatureRows',
  displayName: 'Lifestyle Feature Rows',
  tokens: {},
  slotGroups: {
    'lifestylefeaturerows.row_1': row1Def,
    'lifestylefeaturerows.row_2': row2Def,
    'lifestylefeaturerows.row_3': row3Def,
    'lifestylefeaturerows.row_4': row4Def,
  },
};

(LifestyleFeatureRows as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default LifestyleFeatureRows;
